import { LookupYesNoType } from '@/api/dtos';
import { AppSettings } from '@/AppSettings';
import EConsentChecklist from '@/components/consent/EConsentChecklist';
import EConsentPage from '@/components/consent/EConsentPage';
import EConsentSignatureFields from '@/components/consent/EConsentSignatureFields';
import EConsentSignatureUpdater from '@/components/consent/EConsentSignatureUpdater';
import EConsentTrialDetails from '@/components/consent/EConsentTrialDetails';
import { optionalConsentEConsentRegistration } from '@/components/consent/eConsentFormRegistry';
import EConsentForm from '@/components/forms/EConsentForm';
import EConsentContext, { EConsentUserType } from '@/contexts/EConsentContext';
import { Box, Button, styled, Typography } from '@mui/material';
import { GroupedField, GroupedFields, IFormLabel, ILookup, InstitutionContext, ToggleButtonGroup } from '@ngt/opms';
import { ValidateOn } from '@ngt/forms-core';
import { FC, useContext } from 'react';

const Paragraph = styled(Typography)(({ theme }) => ({ paddingBottom: theme.spacing(1.5) }));
const SectionHeading = styled(Typography)(({ theme }) => ({
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    fontWeight: 'bold',
    [theme.breakpoints.down('sm')]: { width: '80%' },
}));
const PicfButton = styled(Button)(({ theme }) => ({ padding: theme.spacing(1) }));

const fieldGrid = { size: { xs: 12, sm: 12, md: 5, lg: 4, xl: 4 } };
const containerGrid = { sx: { paddingBottom: 1.5, paddingTop: 1.5 } };

const answerItems = [
    { id: LookupYesNoType.Yes, value: 'I agree' },
    { id: LookupYesNoType.No, value: 'I do not agree' },
];

const consentLookups: ILookup[] = [
    { propertyName: 'agreedToTissueSampleStorage', items: answerItems },
    { propertyName: 'agreedToBloodStorage', items: answerItems },
    { propertyName: 'agreedToInfoSharing', items: answerItems },
    { propertyName: 'agreedToWeShareSharing', items: answerItems },
];

const consentLabels: IFormLabel[] = [
    {
        propertyPath: 'agreedToTissueSampleStorage',
        detailedLabel: undefined,
        label: 'Do you agree to the storage of your tissue samples for future unspecified research?',
    },
    {
        propertyPath: 'agreedToBloodStorage',
        detailedLabel: undefined,
        label: 'Do you agree to the storage of your blood samples for future unspecified research?',
    },
    {
        propertyPath: 'agreedToInfoSharing',
        detailedLabel: undefined,
        label: 'Do you agree to information being shared with other researchers in the future?',
    },
    {
        propertyPath: 'agreedToWeShareSharing',
        detailedLabel: undefined,
        label: 'Do you agree to information from WeShare being shared for future unspecified research?',
    },
    { propertyPath: 'patientName', detailedLabel: undefined, label: 'Name' },
    { propertyPath: 'datePatientSignedConsent', detailedLabel: undefined, label: 'Date' },
    { propertyPath: 'investigatorName', detailedLabel: undefined, label: 'Name' },
    { propertyPath: 'dateInvestigatorSignedConsent', detailedLabel: undefined, label: 'Date' },
];

const consentStatements = [
    'I have read the Participant Information Sheet or someone has read it to me in a language that I understand.',
    'I understand the purposes, procedures and risks of the research described in the trial.',
    'I have discussed my participation with a member of the trial team, had the opportunity to ask questions and am satisfied with the answers I received.',
    'I have been informed about the possible risks of taking part in this optional research.',
    'I consent to relevant health professionals, hospitals or laboratories releasing information needed for this research and understand that it will remain confidential.',
    'I have completed the choices below about taking part in this optional component of OPT-Pembro.',
    'I understand that taking part is voluntary and that I am free to withdraw without affecting my future health care.',
    'I understand that I will be given a signed copy of this document to keep.',
];

const OptionalConsentFields: FC = () => {
    const { userType } = useContext(EConsentContext);
    const participantFieldDisabled = userType === EConsentUserType.Investigator;

    return (
        <EConsentForm
            requestTypeName={optionalConsentEConsentRegistration.requestTypeName}
            disableHeading
            disablePaper
            labels={consentLabels}
            lookups={consentLookups}
            validateOn={ValidateOn.onChange}
            debug={AppSettings.debug}
            hideSubmit
            hideCancel
            submitAndReturnButtonText="Submit"
        >
            <EConsentSignatureUpdater />
            <GroupedFields ContainerProps={{ borderBottom: 'none !important', borderTop: 'none !important' }}>
                <SectionHeading>Your choices for this optional research</SectionHeading>
                {[
                    'agreedToTissueSampleStorage',
                    'agreedToBloodStorage',
                    'agreedToInfoSharing',
                    'agreedToWeShareSharing',
                ].map(propertyPath => (
                    <GroupedField
                        key={propertyPath}
                        propertyPath={propertyPath}
                        component={ToggleButtonGroup}
                        FieldGridProps={fieldGrid}
                        ContainerGridProps={containerGrid}
                        disabled={participantFieldDisabled}
                    />
                ))}
                <EConsentSignatureFields
                    investigatorStatement="I have explained this optional research, its procedures and risks to the participant and I believe they have understood that explanation."
                />
            </GroupedFields>
        </EConsentForm>
    );
};

const OptionalConsentEConsentForm: FC = () => {
    const { principalInvestigator, picfDownloadUrl, picfVersion } = useContext(EConsentContext);
    const { data: institution } = useContext(InstitutionContext);

    return (
        <EConsentPage>
            <EConsentTrialDetails details={[
                { label: 'Short Name of Trial', value: 'BCT 2601 OPT-Pembro' },
                {
                    label: 'Full Name of Trial',
                    value: 'Optimizing adjuvant prescription of Pembrolizumab in patients with early-stage triple-negative breast cancer achieving pathologic complete response after standard neoadjuvant chemotherapy and pembrolizumab.',
                },
                { label: 'Principal Investigator', value: principalInvestigator },
                { label: 'Trial sponsor', value: 'Breast Cancer Trials' },
                { label: 'Site Name', value: institution?.name },
            ]} />

            <Paragraph>
                This research is optional. You can take part in OPT-Pembro without agreeing to the storage of
                tissue or blood samples, or to the future sharing of information covered by this consent form.
            </Paragraph>
            <Paragraph>
                The separate Participant Information and Consent Form explains how samples and information may be
                stored and used for future unspecified research. Please read it and discuss any questions with the
                trial team or someone you trust before making your choices.
            </Paragraph>

            {picfVersion && picfDownloadUrl && (
                <Box sx={{ textAlign: 'center', mt: 0.5 }}>
                    <PicfButton
                        color="primary"
                        variant="contained"
                        // @ts-expect-error MUI supports rendering Button as an anchor.
                        component="a"
                        href={picfDownloadUrl}
                        target="_blank"
                        rel="noopener"
                    >
                        {picfVersion}
                    </PicfButton>
                </Box>
            )}

            <SectionHeading>By signing this consent form, I acknowledge that:</SectionHeading>
            <EConsentChecklist items={consentStatements} />
            <OptionalConsentFields />
        </EConsentPage>
    );
};

export default OptionalConsentEConsentForm;
